import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true, // Include cookies for authentication
  timeout: 300000, // 5 minute timeout for bulk operations
  headers: {
    'Content-Type': 'application/json',
  }
});

// Debug logging for development
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Base URL:', process.env.REACT_APP_API_URL || '/api');
}

// Request interceptor for adding auth headers
api.interceptors.request.use(
  (config) => {
    // HTTP-only cookies are handled automatically by withCredentials: true
    // No need to manually add Authorization headers from localStorage
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.log('🚨 API Error:', error.config?.url, error.response?.status, error.message);
    
    // Don't redirect for auth verification or if we're already on login page
    const isVerifyRequest = error.config?.url?.includes('/auth/verify');
    const isOnLoginPage = window.location.pathname === '/admin/login';
    
    if (error.response?.status === 401 && !isVerifyRequest && !isOnLoginPage) {
      console.log('🔄 Redirecting to login due to 401 error');
      // Use a timeout to prevent multiple rapid redirects
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 100);
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  verify: () => api.get('/auth/verify'),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
  getProfile: () => api.get('/auth/profile'),
};

// Tours API
export const toursAPI = {
  getAll: (params = {}) => api.get('/tours', { params }),
  getById: (id) => api.get(`/tours/${id}`),
  create: (tourData) => api.post('/tours', tourData),
  update: (id, tourData) => api.put(`/tours/${id}`, tourData),
  delete: (id) => api.delete(`/tours/${id}`),
  getStatistics: (id) => api.get(`/tours/${id}/statistics`),
  getSummary: () => api.get('/tours/summary'),
  updateStatus: (id, status) => api.patch(`/tours/${id}/status`, { status }),
};

// Travelers API
export const travelersAPI = {
  getAll: (params = {}) => api.get('/travelers', { params }),
  getById: (id) => api.get(`/travelers/${id}`),
  getByUUID: (uuid) => api.get(`/travelers/uuid/${uuid}`),
  create: (travelerData) => api.post('/travelers', travelerData),
  update: (id, travelerData) => api.put(`/travelers/${id}`, travelerData),
  delete: (id) => api.delete(`/travelers/${id}`),
  generateQRCode: (id) => api.get(`/travelers/${id}/qr-code`),
  bulkUpdate: (travelerIds, updateData) => api.put('/travelers/bulk-update', { traveler_ids: travelerIds, update_data: updateData }),
  bulkUpdateRooms: (updates) => api.patch('/travelers/bulk-rooms', { updates }),
  getTourStatistics: (tourId) => api.get(`/travelers/tour/${tourId}/statistics`),
  getStatistics: () => api.get('/travelers/statistics'),
  exportCSV: (params = {}) => api.get('/travelers/export/csv', { params, responseType: 'blob' }),
  downloadTemplate: () => api.get('/travelers/export/template', { responseType: 'blob' }),
  getIncomplete: (params = {}) => api.get('/travelers/incomplete', { params }),
  markComplete: (id) => api.put(`/travelers/${id}/complete`),
  bulkMarkComplete: (travelerIds) => api.put('/travelers/bulk-complete', { traveler_ids: travelerIds }),
  import: (travelerData) => api.post('/travelers/import', travelerData),
  bulkImport: (csvFile, tourId = null) => {
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    if (tourId) {
      formData.append('tour_id', tourId);
    }
    return api.post('/travelers/bulk-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// Documents API
export const documentsAPI = {
  getAll: (params = {}) => api.get('/documents', { params }),
  getByTravelerId: (travelerId) => api.get(`/documents/traveler/${travelerId}`),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  view: (id) => api.get(`/documents/${id}/view`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/documents/${id}`),
  getStatistics: () => api.get('/documents/statistics'),
};

// PDF Documents API
export const pdfDocumentsAPI = {
  compileByFlight: (params) => api.get('/pdf-documents/compile-by-flight', { 
    params, 
    responseType: 'blob' 
  }),
  getFlights: (tourId) => api.get(`/pdf-documents/flights/${tourId}`),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getTourAnalytics: (tourId) => api.get(`/analytics/tour/${tourId}`),
};

// Settings API
export const settingsAPI = {
  getSettings: () => api.get('/settings'),
  updateSettings: (settings) => api.put('/settings', { settings }),
  getUsers: () => api.get('/settings/users'),
  createUser: (userData) => api.post('/settings/users', userData),
  updateUser: (id, userData) => api.put(`/settings/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/settings/users/${id}`),
  updateProfile: (profileData) => api.put('/settings/profile', profileData),
  createBackup: () => api.post('/settings/backup'),
  getSystemInfo: () => api.get('/settings/system-info'),
  testTelegramBot: (botData) => api.post('/settings/test-telegram-bot', botData),
  // Password management functions
  changeUserPassword: (userId, newPassword) => api.put(`/settings/users/${userId}/password`, { newPassword }),
  resetPassword: (username, email) => api.post('/settings/reset-password', { username, email }),
};

// Logo API
export const logoAPI = {
  getCompanyLogo: () => api.get('/logos/company'),
  uploadCompanyLogo: (formData) => api.post('/logos/company/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteCompanyLogo: () => api.delete('/logos/company'),
  uploadTourLogo: (tourId, formData) => api.post(`/logos/tour/${tourId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTourLogo: (tourId) => api.delete(`/logos/tour/${tourId}`),
};

// Telegram Bot API
export const telegramBotAPI = {
  getConfig: () => api.get('/telegram-bot/config'),
  updateConfig: (config) => api.put('/telegram-bot/config', config),
  testBot: (botToken) => api.post('/telegram-bot/test', { botToken }),
  getQuestionTree: () => api.get('/telegram-bot/question-tree'),
  createQuestionNode: (node) => api.post('/telegram-bot/question-nodes', node),
  updateQuestionNode: (nodeKey, node) => api.put(`/telegram-bot/question-nodes/${nodeKey}`, node),
  deleteQuestionNode: (nodeKey) => api.delete(`/telegram-bot/question-nodes/${nodeKey}`),
  getTours: () => api.get('/telegram-bot/tours'),
  getDynamicContent: () => api.get('/telegram-bot/dynamic-content'),
  createDynamicContent: (content) => api.post('/telegram-bot/dynamic-content', content),
  getSessions: (params = {}) => api.get('/telegram-bot/sessions', { params }),
  getAnalytics: () => api.get('/telegram-bot/analytics'),
};

// Generic error handler
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
    console.log('API Error Details:', error.response.data); // Debug log
    throw new Error(message);
  } else if (error.request) {
    // Request made but no response received
    throw new Error('Network error - please check your connection');
  } else {
    // Something else happened
    throw new Error('An unexpected error occurred');
  }
};

// Utility function for handling API calls with loading states
export const apiCall = async (apiFunction, ...args) => {
  try {
    const response = await apiFunction(...args);
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
};

export default api;